import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngxs/store";
import { DatasetsService } from "src/app/shared/services/datasets.service";
import { LocalDatasetsService } from "src/app/shared/services/local-datasets.service";
import { DashboardDataController } from "../data_controller/dashboard_data_controller";
import { ProductMethodologiesLimiterService } from "../services/product-methodologies-limiter.service";
import { DashboardDataTransformer } from "../data_transformers/dashboard_data_transformer";
import  { Location } from "@angular/common"

export abstract class ChartConfigService {
  readonly granularityId: Granularity;
  readonly metricId: Metric;

  protected constructor({ granularityId, metricId }: DecodedDashboardIdEnum) {
    this.granularityId = granularityId;
    this.metricId = metricId;
  }

  abstract getHeading(filterValues: Map<string, any>): string
  abstract getMapHeading(filterValues: Map<string, any>): string
  abstract getTransformers(): DashboardDataTransformer[]
  abstract getConfiguration(): any
}

export interface SeriesFilterDetail {
  filterName: string;
  filterValueKey: string;
}

export interface MetricFilterDetail {
  filterName: string;
  filterValueKey: string;
}

export enum Analysis {
  Dossier = 1,
  TimeSeries = 2,
  Ranking = 3,
  Comparison = 4,
  Forecast = 5,
  Gender = 6,
  Index = 7
}

export enum Granularity {
  States = 3,
  WzClassStates = 312,
  Districts = 4,
  IndustrySubMarkets = 10,
  SubMarkets = 11,
  DistrictSubMarkets = 114,
  WzClasses = 12,
  DistrictWzClasses = 124,
  Unknown = 0
}

export enum Metric {
  CompaniesCount = 1,
  CompaniesRevenue = 2,
  GrossValueAdded = 3,
  EconomicParticipant = 4,
  Employed = 5,
  SelfEmployed = 7,
  Tourism = 12
}

export type DecodedDashboardIdEnum = {
  analysisId: Analysis,
  granularityId: Granularity,
  metricId: Metric
}

/**
 * Defines the full set of dependency-injected services for a dashboard component.
 * This interface should mirror the constructor of the component that uses it.
 * Note: The original constructor had two 'ActivatedRoute' injections ('activatedRoute' and 'route').
 * They are represented here by a single property for clarity.
 */
export interface DashboardConfigurationDependencies {
  datasetsService: DatasetsService;
  localDatasetsService: LocalDatasetsService;
  dashboardDataController: DashboardDataController;
  activatedRoute: ActivatedRoute;
  store: Store;
  router: Router;
  methodologiesLimiterService: ProductMethodologiesLimiterService;
  sumSubMarketIds: string[];
  datasetName: string;
  granularityId: Granularity,
  metricId: Metric,
  location: Location
}

export const RankingChartValueKeys = {
  valueKey: 'value',
  valuePctKey: 'value_pct',
  rankKey: 'rank'
}

export function getMetricValuesByMetricId(metricId: Metric) {
  const CompaniesCountMetricValues = {
    COMPANIES_COUNT: 'companies_count',
    SMALL_COMPANIES_COUNT: 'small_companies_count',
  } as const

  const CompaniesRevenueMetricValues = {
    COMPANIES_REVENUE: 'companies_revenue__k_eur',
    SMALL_COMPANIES_REVENUE: 'small_companies_revenue__k_eur',
  } as const

  const EconomicParticipantMetricValues = {
    TOTAL: 'economic_participants_total',
    CORE_MARKET: 'economic_participants_core_market',
    MARGINAL: 'economic_participants_marginal',
  } as const

  const EmployedMetricValues = {
    TOTAL: 'employed_total',
    CORE_MARKET: 'employed_social_security',
    MARGINAL: 'employed_marginal',
  } as const

  const GrossValueAddedMetricValues = {
    GROSS_VALUE: 'gross_value_added__k_eur',
    PRODUCTION_VALUE: 'production_value__k_eur',
  } as const

  const SelfEmployedValues = {
    SELF_EMPLOYED_COUNT: 'solo_self_employed_count',
  } as const

  const TourismValues = {
    OVERNIGHT_STAYS_TOTAL: '',
    OVERNIGHT_STAYS_DOMESTIC: '',
    OVERNIGHT_STAYS_FOREIGN: '',
  }

  type MetricValues = typeof CompaniesCountMetricValues | typeof CompaniesRevenueMetricValues | typeof EconomicParticipantMetricValues | typeof EmployedMetricValues | typeof SelfEmployedValues | typeof TourismValues | typeof GrossValueAddedMetricValues

  const MetricValuesMap: Record<Metric, MetricValues> = {
    [Metric.CompaniesCount]: CompaniesCountMetricValues,
    [Metric.CompaniesRevenue]: CompaniesRevenueMetricValues,
    [Metric.EconomicParticipant]: EconomicParticipantMetricValues,
    [Metric.Employed]: EmployedMetricValues,
    [Metric.SelfEmployed]: SelfEmployedValues,
    [Metric.Tourism]: TourismValues,
    [Metric.GrossValueAdded]: GrossValueAddedMetricValues
  }

  return MetricValuesMap[metricId];
}